import React from 'react';
import { Link } from 'react-router-dom';

function Security() {
  return (
    <div className="doc-page">
      {/* Navigation */}
      <nav className="nav">
        <div className="nav-container">
          <Link to="/" className="nav-logo">
            <span className="logo-text">AVALW</span>
            <span className="logo-badge">Worker</span>
          </Link>
          <div className="nav-links">
            <Link to="/transparency" className="nav-link">Transparency</Link>
            <Link to="/protocol" className="nav-link">Protocol</Link>
            <Link to="/security" className="nav-link">Security</Link>
            <Link to="/dashboard" className="nav-btn">Dashboard</Link>
          </div>
        </div>
      </nav>

      <div className="doc-container">
        <div className="doc-header">
          <h1 className="doc-title">Security & Privacy</h1>
          <p className="doc-subtitle">
            Our security practices, privacy policies, and how we protect both workers and API users.
          </p>
        </div>

        {/* Transport Security */}
        <section className="doc-section">
          <h2>Transport Security</h2>
          <p>All communications between workers, API clients, and our servers are encrypted.</p>

          <table className="doc-table">
            <thead>
              <tr>
                <th>Feature</th>
                <th>Configuration</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Minimum TLS Version</td>
                <td><code>TLS 1.2</code></td>
              </tr>
              <tr>
                <td>Preferred TLS Version</td>
                <td><code>TLS 1.3</code></td>
              </tr>
              <tr>
                <td>Certificate</td>
                <td>Let's Encrypt (auto-renewed)</td>
              </tr>
              <tr>
                <td>HSTS</td>
                <td>Enabled (max-age=31536000)</td>
              </tr>
            </tbody>
          </table>
        </section>

        {/* Authentication */}
        <section className="doc-section">
          <h2>Authentication</h2>

          <h3>Account Hash</h3>
          <p>Your account is identified by a SHA-256 hash:</p>
          <ul>
            <li>Generated from 32 random bytes (256 bits of entropy)</li>
            <li>Unique per account</li>
            <li>Not derived from any personal information</li>
          </ul>

          <h3>Worker Token</h3>
          <p>API authentication uses a 96-character hex token:</p>
          <ul>
            <li>Generated from 48 random bytes (384 bits of entropy)</li>
            <li>Used for API requests and worker heartbeats</li>
            <li>Can be regenerated from dashboard if compromised</li>
          </ul>

          <div className="info-box warning">
            <div className="info-box-title">Keep your tokens secure!</div>
            <p>Anyone with your worker token can make API requests using your quota.</p>
          </div>
        </section>

        {/* Data & Privacy */}
        <section className="doc-section">
          <h2>Data & Privacy</h2>

          <h3>What We Store</h3>
          <table className="doc-table">
            <thead>
              <tr>
                <th>Data</th>
                <th>Retention</th>
                <th>Purpose</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Account Hash</td>
                <td>Permanent</td>
                <td>Account identification</td>
              </tr>
              <tr>
                <td>Worker Token</td>
                <td>Permanent</td>
                <td>API authentication</td>
              </tr>
              <tr>
                <td>System Info</td>
                <td>Last heartbeat only</td>
                <td>Tier calculation</td>
              </tr>
              <tr>
                <td>Core-Hours</td>
                <td>Daily (resets midnight UTC)</td>
                <td>Tier qualification</td>
              </tr>
              <tr>
                <td>API Usage Count</td>
                <td>Daily (resets midnight UTC)</td>
                <td>Rate limiting</td>
              </tr>
            </tbody>
          </table>

          <h3>What We Do NOT Store</h3>
          <div className="info-box success">
            <div className="info-box-title">Privacy First</div>
            <p>We do not collect or store: IP addresses, search queries, user agents, click history, geolocation, or any personal information.</p>
          </div>
        </section>

        {/* Worker Security */}
        <section className="doc-section">
          <h2>Worker Security</h2>

          <h3>Process Isolation</h3>
          <p>The worker runs as a standard Node.js process with no elevated privileges:</p>
          <ul>
            <li>No root/admin access required</li>
            <li>No system modifications</li>
            <li>No network listeners (outbound only)</li>
            <li>No file system access (except optional logs)</li>
          </ul>

          <h3>Resource Limits</h3>
          <table className="doc-table">
            <tbody>
              <tr>
                <td>Max CPU Usage</td>
                <td><code>50%</code> (target)</td>
              </tr>
              <tr>
                <td>Max Memory</td>
                <td><code>512 MB</code></td>
              </tr>
              <tr>
                <td>Network</td>
                <td>~1 MB/minute</td>
              </tr>
              <tr>
                <td>Disk Writes</td>
                <td>0 (no disk usage)</td>
              </tr>
            </tbody>
          </table>
        </section>

        {/* API Security */}
        <section className="doc-section">
          <h2>API Security</h2>

          <h3>Rate Limiting</h3>
          <ul>
            <li>Daily limits reset at midnight UTC</li>
            <li>Exceeding limits returns <code>429 Too Many Requests</code></li>
            <li>No bursting above limit is allowed</li>
          </ul>

          <h3>Domain Whitelisting</h3>
          <p>When domains are whitelisted, requests are validated against:</p>
          <ul>
            <li><code>Origin</code> header</li>
            <li><code>Referer</code> header</li>
          </ul>
          <p>Requests from non-whitelisted domains will return <code>403 Forbidden</code>.</p>
        </section>

        {/* Security Reporting */}
        <section className="doc-section">
          <h2>Security Reporting</h2>

          <div className="info-box info">
            <div className="info-box-title">Found a vulnerability?</div>
            <p>Please report it responsibly to <a href="mailto:security@avalw.com">security@avalw.com</a>. We respond within 48 hours.</p>
          </div>

          <h3>What to Include</h3>
          <ul>
            <li>Description of the vulnerability</li>
            <li>Steps to reproduce</li>
            <li>Potential impact</li>
            <li>Any proof-of-concept code (if applicable)</li>
          </ul>
        </section>

        {/* Back Link */}
        <div style={{ marginTop: '3rem', paddingTop: '2rem', borderTop: '1px solid #e2e8f0' }}>
          <Link to="/" className="btn btn-outline">Back to Home</Link>
        </div>
      </div>
    </div>
  );
}

export default Security;
